/**
 * ClientOnboardingTerms Component
 * Terms and Conditions agreement page for client onboarding
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TERMS_AND_CONDITIONS } from '../../constants/termsAndConditions';
import { OnboardingProgressBar } from '../../components/ui';
import publicTokenService from '../../services/publicTokenService';
import styles from './ClientOnboardingTerms.module.css';

const ClientOnboardingTerms = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [agreed, setAgreed] = useState(false);
    const [showWarning, setShowWarning] = useState(false);
    const [isTestMode] = useState(sessionStorage.getItem('onboarding_test_mode') === 'true');
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
    const termsContentRef = React.useRef(null);

    // Load saved agreement state
    useEffect(() => {
        const saved = sessionStorage.getItem(`onboarding_terms_agreed_${token}`);
        if (saved === 'true') {
            setAgreed(true);
            setHasScrolledToBottom(true); // If they already agreed, they've read it
        }
    }, [token]);

    // Check if user has scrolled to the bottom of terms
    useEffect(() => {
        const termsElement = termsContentRef.current;
        if (!termsElement) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = termsElement;
            // Check if scrolled to within 10px of the bottom
            if (scrollHeight - scrollTop - clientHeight < 10) {
                setHasScrolledToBottom(true);
            }
        };

        // Check on mount in case content doesn't need scrolling
        if (termsElement.scrollHeight <= termsElement.clientHeight) {
            setHasScrolledToBottom(true);
        }

        termsElement.addEventListener('scroll', handleScroll);
        return () => termsElement.removeEventListener('scroll', handleScroll);
    }, []);

    const handleSaveAndExit = async () => {
        if (agreed) {
            try {
                // Save to backend (step 1: Terms)
                await publicTokenService.saveProgress(token, {
                    stepNumber: 1,
                    stepData: { termsAgreed: true },
                    markStepComplete: false,
                    moveToNextStep: false
                });
                
                sessionStorage.setItem(`onboarding_terms_agreed_${token}`, 'true');
                alert('Your progress has been saved. You can return using the same link to continue.');
            } catch (error) {
                console.error('Failed to save progress:', error);
                alert('Failed to save progress. Please try again.');
            }
        } else {
            alert('Please agree to the terms before saving.');
        }
    };

    const handleAgree = async () => {
        if (agreed) {
            try {
                // Save to backend and mark step complete
                await publicTokenService.saveProgress(token, {
                    stepNumber: 1,
                    stepData: { termsAgreed: true },
                    markStepComplete: true,
                    moveToNextStep: true
                });
                
                // Store agreement in sessionStorage
                sessionStorage.setItem(`onboarding_terms_agreed_${token}`, 'true');
                navigate(`/client-onboarding/${token}/account-type`);
            } catch (error) {
                console.error('Failed to save progress:', error);
                alert('Failed to save. Please try again.');
            }
        }
    };

    const handleDisagree = () => {
        setShowWarning(true);
    };

    const handleGoBack = () => {
        navigate(`/client-onboarding/${token}`);
    };

    return (
        <React.Fragment>
            <OnboardingProgressBar />
            <div className={styles.container}>
                <div className={styles.header}>
                <div className={styles.logoHeader}>
                    <img 
                        src="/Alt Logo Flat.png" 
                        alt="WETLAB" 
                        className={styles.logoHeaderImg}
                    />
                </div>
            </div>

            <div className={styles.content}>
                <div className={styles.termsContainer}>
                    <div className={styles.termsHeader}>
                        <h1 className={styles.title}>{TERMS_AND_CONDITIONS.title}</h1>
                        <p className={styles.lastUpdated}>
                            Last Updated: {new Date(TERMS_AND_CONDITIONS.lastUpdated).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}
                        </p>
                    </div>

                    <div className={styles.termsContent} ref={termsContentRef}>
                        {TERMS_AND_CONDITIONS.content.split('\n\n').map((paragraph, index) => (
                            <p key={index} className={styles.paragraph}>
                                {paragraph}
                            </p>
                        ))}
                    </div>

                    {!hasScrolledToBottom && (
                        <div className={styles.scrollNotice}>
                            <span className={styles.scrollIcon}>👇</span>
                            Please scroll down to read all terms and conditions
                        </div>
                    )}

                    <div className={styles.agreementSection}>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={agreed}
                                onChange={(e) => {
                                    setAgreed(e.target.checked);
                                    if (e.target.checked) setShowWarning(false);
                                }}
                                className={styles.checkbox}
                                disabled={!hasScrolledToBottom}
                                title={!hasScrolledToBottom ? 'Please scroll to the bottom to enable' : ''}
                            />
                            <span className={styles.checkboxText}>
                                I have read and agree to the Terms and Conditions
                            </span>
                        </label>
                    </div>

                    {showWarning && (
                        <div className={styles.warningBox}>
                            <div className={styles.warningIcon}>⚠️</div>
                            <div className={styles.warningContent}>
                                <h3>Agreement Required</h3>
                                <p>
                                    You must agree to the Terms and Conditions to proceed with the onboarding process. 
                                    If you have questions or concerns, please contact us at{' '}
                                    <a href="mailto:support@wetlab.com">support@wetlab.com</a> before continuing.
                                </p>
                            </div>
                        </div>
                    )}

                    <div className={styles.actions}>
                        <button
                            onClick={handleGoBack}
                            className={styles.backButton}
                        >
                            ← Go Back
                        </button>
                        <div className={styles.actionButtons}>
                            <button
                                onClick={handleSaveAndExit}
                                className={styles.saveButton}
                                disabled={!agreed}
                            >
                                💾 Save and Continue Later
                            </button>
                            <button
                                onClick={handleDisagree}
                                className={styles.disagreeButton}
                            >
                                I Disagree
                            </button>
                            <button
                                onClick={handleAgree}
                                className={styles.agreeButton}
                                disabled={!agreed}
                            >
                                I Agree - Continue
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            </div>

        <div className={styles.footer}>
            <p className={styles.footerText}>
                Questions? Contact us at <a href="mailto:support@wetlab.com">support@wetlab.com</a>
            </p>
        </div>
        </React.Fragment>
    );
};

export default ClientOnboardingTerms;
